/**
 * Hugging Face Hub access — the only module in the app that touches the network.
 *
 * Every rule from the plan's "Hub etiquette" section is enforced here rather
 * than left to callers, because a caller that forgets one costs the Hub real
 * bandwidth:
 *
 *   1. Payloads are fetched with `Range` only — `fetchRange` is the sole way out
 *      of this module, and there is deliberately no "fetch the whole file" door.
 *   2. A revision is resolved to an immutable commit SHA exactly once per repo,
 *      and every subsequent URL uses that SHA, so the CDN can actually cache.
 *   3. At most MAX_IN_FLIGHT requests are in the air at a time.
 *   4. A 429 is surfaced as a typed, retry-after-carrying error so the UI can
 *      say so instead of hammering.
 *   5. Nothing here crawls, prefetches, or refreshes in the background. Every
 *      request traces back to something the visitor clicked.
 *
 * No token support, by design (plan §6): a paste-your-token box on an unknown
 * site reads as phishing. Public repos only. If gated access ever earns its
 * place, it arrives as official HF OAuth, not a text input.
 */

import {
  CspBlockedError,
  installCspWatch,
  recentConnectViolation,
} from "./csp-watch.js";

export const HUB_ORIGIN = "https://huggingface.co";

// Start watching before the first request, so a violation on the very first
// fetch is already being recorded when it fires.
installCspWatch();

/** Plan §5.3: at most four requests in flight at any moment. */
const MAX_IN_FLIGHT = 4;

/** A rate-limit response, distinguished so the UI can back off and explain. */
export class HubRateLimitError extends Error {
  readonly retryAfterSeconds: number | undefined;
  constructor(retryAfterSeconds: number | undefined) {
    super(
      retryAfterSeconds === undefined
        ? "The Hub is rate-limiting this browser (HTTP 429). Give it a moment before retrying."
        : `The Hub is rate-limiting this browser (HTTP 429). Retry in about ${retryAfterSeconds}s.`,
    );
    this.name = "HubRateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * No public model at this id.
 *
 * The Hub answers **401, not 404**, for a repo that does not exist — the body is
 * `{"error":"Invalid username or password."}` — because "does not exist" and
 * "exists but you may not see it" are deliberately indistinguishable to an
 * anonymous caller. That is correct of them and unhelpful to us, so this error
 * says both possibilities rather than guessing one.
 */
export class RepoNotFoundError extends Error {
  readonly repoId: string;
  constructor(repoId: string, revision: string) {
    super(
      // Two lines, and the break is load-bearing. The first names what failed,
      // which is detail; the second is the one instruction the visitor can act
      // on, and it was previously buried mid-sentence behind a true but
      // unhelpful aside about how the Hub represents private repos. A reader
      // scanning an error reads the bold line and stops.
      `No public model at "${repoId}" (revision "${revision}").\n\n**Check the spelling.**`,
    );
    this.name = "RepoNotFoundError";
    this.repoId = repoId;
  }
}

/**
 * A gated repo — readable metadata, unreadable weights.
 *
 * Detected from the `gated` field the revision endpoint already returns, which
 * is why this is not a 401 surfaced late. A gated repo answers **200** for its
 * metadata and **401** for every file, so without this check the visitor gets
 * "Could not read model.safetensors.index.json: HTTP 401" — an internal
 * filename and a status code, for a situation that is not an error on their
 * part and not a bug on ours.
 *
 * Plan §12.5 is explicit that this case is a *feature explanation*, not a
 * failure: TensorLens has no token input by design (§6), so a gated repo is
 * simply outside what it can read, and saying so plainly is the whole job.
 */
export class HubGatedError extends Error {
  readonly repoId: string;
  /** The Hub's own value — `"auto"`, `"manual"`, or similar. */
  readonly gate: string;
  constructor(repoId: string, gate: string) {
    super(
      `"${repoId}" is a gated model (${gate} approval). Its weights need an access token, and TensorLens deliberately has no token input — pasting a token into an unfamiliar site is a habit worth not teaching. Nothing is broken; this model is simply outside what it can read.`,
    );
    this.name = "HubGatedError";
    this.repoId = repoId;
    this.gate = gate;
  }
}

export class HubHttpError extends Error {
  readonly status: number;
  constructor(status: number, url: string) {
    super(`Hub request failed: HTTP ${status} for ${url}`);
    this.name = "HubHttpError";
    this.status = status;
  }
}

/**
 * A counting semaphore over in-flight requests.
 *
 * Deliberately global to the module, not per-repo: the cap the Hub cares about
 * is on this browser tab as a whole, so compare mode (two repos at once) must
 * share one budget rather than getting two.
 */
let inFlight = 0;
const waiting: Array<() => void> = [];

async function acquireSlot(): Promise<void> {
  if (inFlight < MAX_IN_FLIGHT) {
    inFlight += 1;
    return;
  }
  await new Promise<void>((resolve) => waiting.push(resolve));
  inFlight += 1;
}

function releaseSlot(): void {
  inFlight -= 1;
  const next = waiting.shift();
  if (next) next();
}

async function hubFetch(url: string, init?: RequestInit): Promise<Response> {
  await acquireSlot();
  try {
    let response: Response;
    try {
      response = await fetch(url, init);
    } catch (cause) {
      // A CSP refusal and a dead network are the same bare `TypeError` here —
      // the spec withholds the reason on purpose. `csp-watch` listens for the
      // `securitypolicyviolation` event, which is the only reliable way to tell
      // them apart, and turns an unanswerable "failed to fetch" into a sentence
      // naming the host our own policy refused.
      if (cause instanceof TypeError) {
        const violation = recentConnectViolation(url);
        if (violation) throw new CspBlockedError(url, violation);
      }
      throw cause;
    }
    if (response.status === 429) {
      const header = response.headers.get("retry-after");
      const seconds = header === null ? Number.NaN : Number(header);
      throw new HubRateLimitError(Number.isFinite(seconds) ? seconds : undefined);
    }
    if (!response.ok) {
      throw new HubHttpError(response.status, url);
    }
    return response;
  } finally {
    releaseSlot();
  }
}

/** A repo pinned to an immutable commit — the only thing the rest of the app quotes. */
export interface ResolvedRepo {
  readonly repoId: string;
  /** The revision the visitor asked for (`main`, a tag, a branch, or a SHA). */
  readonly requestedRevision: string;
  /** The commit SHA it resolved to. Every file URL below is built from this. */
  readonly sha: string;
  /** Every file in the repo at that commit. */
  readonly files: readonly string[];
  /**
   * The Hub's `gated` value when the repo is gated, otherwise `false`.
   *
   * Kept on the resolved repo rather than thrown away because a caller that
   * already holds one should not have to re-ask the Hub to find out.
   */
  readonly gated: string | false;
  /**
   * The model card's parsed frontmatter, as the Hub returns it.
   *
   * Kept because it carries `base_model` — the only provenance a safetensors or
   * MLX repo has, since their `__metadata__` names the producing framework and
   * nothing about lineage. It arrives in this response whether or not anyone
   * reads it, so keeping it costs nothing and fetching it separately later
   * would cost a request (plan §5: every request traces to a visitor action,
   * and this one already did).
   *
   * Deliberately `unknown`. It is author-written YAML with no schema the Hub
   * enforces, so anything that reads it validates as it goes —
   * `hf/provenance.ts` does exactly that.
   */
  readonly cardData: unknown;
}

/**
 * Resolve `repoId` at `revision` to an immutable commit SHA and file list.
 *
 * This is the one non-Range request the app makes, and it happens once per repo
 * per session. Everything after it addresses immutable URLs, which is what makes
 * repeat views free for the Hub's CDN (plan §5.2).
 */
export async function resolveRepo(
  repoId: string,
  revision = "main",
): Promise<ResolvedRepo> {
  const url = `${HUB_ORIGIN}/api/models/${encodeRepoId(repoId)}/revision/${encodeURIComponent(revision)}`;
  let response: Response;
  try {
    response = await hubFetch(url);
  } catch (cause) {
    // 401 and 404 both mean "nothing public here" — see RepoNotFoundError for
    // why the Hub prefers 401. Translated at the boundary so no caller has to
    // know that, and so the visitor never sees an internal API URL.
    if (
      cause instanceof HubHttpError &&
      (cause.status === 401 || cause.status === 404)
    ) {
      throw new RepoNotFoundError(repoId, revision);
    }
    throw cause;
  }
  const info = (await response.json()) as {
    id?: unknown;
    sha?: unknown;
    gated?: unknown;
    siblings?: Array<{ rfilename?: unknown }>;
    cardData?: unknown;
  };

  // Gating is decided here, before a single payload read. A gated repo answers
  // 200 for this request and 401 for every file, so checking later would spend
  // a request to learn something this response already told us — and would
  // report it as a file error rather than as the access explanation it is.
  const gated =
    typeof info.gated === "string" && info.gated !== "false" ? info.gated : false;
  if (gated !== false) {
    throw new HubGatedError(repoId, gated);
  }

  if (typeof info.sha !== "string") {
    throw new Error(`Hub returned no commit SHA for ${repoId}@${revision}`);
  }
  const files = (info.siblings ?? [])
    .map((s) => s.rfilename)
    .filter((f): f is string => typeof f === "string");

  // Adopt the Hub's canonical id when it gives one.
  //
  // Legacy single-segment ids still resolve — `bert-base-uncased` answers a 307
  // to `google-bert/bert-base-uncased` — and this response carries the canonical
  // form, so taking it costs nothing on a request we were making anyway.
  //
  // Two things follow. A shared link gets the canonical two-segment path rather
  // than falling back to the legacy `?repo=` form, which is what makes that form
  // purely a compatibility shim rather than load-bearing for new links. And
  // every subsequent `fileUrl` addresses the canonical id, so payload reads stop
  // paying a redirect each — a real etiquette improvement, not just tidiness.
  const canonicalId =
    typeof info.id === "string" && info.id.includes("/") ? info.id : repoId;

  return {
    repoId: canonicalId,
    requestedRevision: revision,
    sha: info.sha,
    files,
    gated,
    cardData: info.cardData,
  };
}

/** Immutable URL for one file at the resolved commit. */
export function fileUrl(repo: ResolvedRepo, path: string): string {
  const encodedPath = path.split("/").map(encodeURIComponent).join("/");
  return `${HUB_ORIGIN}/${encodeRepoId(repo.repoId)}/resolve/${repo.sha}/${encodedPath}`;
}

/**
 * Fetch exactly `[start, end)` of a file. The single network primitive for
 * payload bytes; there is no whole-file counterpart on purpose.
 *
 * A server that ignores `Range` answers 200 with the entire file instead of 206
 * with a slice. That is precisely the case this must not silently tolerate — it
 * would mean quietly downloading a multi-gigabyte shard — so a non-206 response
 * to a ranged request is an error, not something to work around.
 */
export async function fetchRange(
  url: string,
  start: number,
  end: number,
): Promise<ArrayBuffer> {
  return (await fetchRangeWithTotal(url, start, end)).bytes;
}

export interface RangeResult {
  readonly bytes: ArrayBuffer;
  /**
   * The whole file's size, from this response's own `Content-Range`.
   *
   * `undefined` only if a server answers 206 without a parseable total, which
   * the spec permits (`bytes 0-63/*`) and no Hub response does.
   */
  readonly totalBytes: number | undefined;
}

/**
 * The same request, also reporting the file's total size.
 *
 * **Not a whole-file counterpart.** It fetches exactly the same range and reads
 * one header off the response it already received — `Content-Range: bytes
 * 0-63/7165121600` names the total on every 206, which is the point. No extra
 * request, no extra byte, and the `Range`-only invariant is untouched.
 *
 * It exists because a GGUF file's *last* tensor has no next offset to measure
 * against, so its extent is the file's size minus where it starts.
 *
 * The alternative routes were both worse. The Hub's `siblings` list carries
 * only `rfilename` **on the default call** — sizes are there behind
 * `?blobs=true`, which is how the Kimi file-size sum was made — so that route
 * costs a second, larger request for a fact already in hand. A `HEAD` costs a
 * second request for the same reason. Recorded precisely because an earlier
 * version of this comment said `siblings` carries no size at all, which is true
 * of the call we make and false of the endpoint (reviewer, 2026-08-09).
 */
export async function fetchRangeWithTotal(
  url: string,
  start: number,
  end: number,
): Promise<RangeResult> {
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end <= start) {
    throw new Error(`Invalid byte range [${start}, ${end})`);
  }
  const response = await hubFetch(url, {
    // HTTP ranges are inclusive on both ends; ours are half-open.
    headers: { Range: `bytes=${start}-${end - 1}` },
  });
  if (response.status !== 206) {
    throw new Error(
      `Expected HTTP 206 (partial content) but got ${response.status} — the origin ignored the Range header, which would mean downloading the whole file. Refusing.`,
    );
  }
  return {
    bytes: await response.arrayBuffer(),
    totalBytes: parseContentRangeTotal(response.headers.get("content-range")),
  };
}

/** `bytes 0-63/7165121600` -> 7165121600; `bytes 0-63/*` and junk -> undefined. */
export function parseContentRangeTotal(header: string | null): number | undefined {
  const match = /\/\s*(\d+)\s*$/.exec(header ?? "");
  if (!match) return undefined;
  const total = Number(match[1]);
  return Number.isSafeInteger(total) && total > 0 ? total : undefined;
}

/**
 * A repo id is `owner/name`; both halves may need escaping but the slash between
 * them must survive, so this cannot be a plain `encodeURIComponent`.
 */
function encodeRepoId(repoId: string): string {
  return repoId.split("/").map(encodeURIComponent).join("/");
}

/**
 * One path segment of a repo id, and how long it may be.
 *
 * Mirrors what the Hub itself accepts. Kept beside `normalizeRepoId` because
 * the two are halves of one question — what may a repo id be — and splitting
 * them across packages is how a path one layer refuses becomes a value another
 * happily uses.
 */
const REPO_SEGMENT = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;
const MAX_REPO_SEGMENT = 128;

/**
 * Is this a repo id we are willing to send to the Hub?
 *
 * Stricter than `normalizeRepoId`, which only settles *shape*: this refuses
 * traversal-like forms and anything outside the Hub's own charset. It lived in
 * `og/src/card-path.ts` — where the card URL parser needs it — and moved here
 * when `hf/provenance.ts` needed the same predicate, because `site` is the
 * package the others import from and the reverse direction is not available.
 * `card-path.ts` re-exports it, so every existing caller is unchanged.
 *
 * `..` is refused **anywhere, even inside a segment**: a leading dot is already
 * out via the charset, but `a..b` is not, and a name that reads as traversal is
 * worth refusing whether or not any particular consumer could be walked with
 * it. The cost is rejecting a repo id nobody has.
 */
export function isCanonicalRepoId(repoId: string): boolean {
  const segments = repoId.split("/");
  if (segments.length !== 2) return false;
  if (repoId.includes("..")) return false;
  return segments.every((s) => REPO_SEGMENT.test(s) && s.length <= MAX_REPO_SEGMENT);
}

/**
 * Parse and validate the repo id a visitor typed.
 *
 * Deliberately *more forgiving than the router* (plan §22.1). The URL scheme
 * requires exactly two path segments, so `tensorlens.dev/bert-base-uncased` is
 * a reserved site page and never that model — but `bert-base-uncased` is still
 * a real Hub id that people paste, and rejecting it teaches nothing.
 *
 * A single-segment id is therefore returned **as-is and resolved by the Hub's
 * own redirect**: `/api/models/bert-base-uncased/revision/main` answers 307 to
 * `/api/models/google-bert/bert-base-uncased/revision/main`, and
 * `/bert-base-uncased/resolve/<sha>/…` likewise redirects with the `Range`
 * header intact, so `resolveRepo` and every payload read work unchanged. We do
 * not canonicalize it ourselves because that would cost an extra Hub request to
 * learn something the Hub is already telling us for free on the request we were
 * making anyway. The cost is one redirect per request for these few legacy ids,
 * and a URL that stays in the legacy `?repo=` form (see `buildUrl`).
 */
export function normalizeRepoId(input: string): string {
  let value = input.trim();

  // Accept a pasted Hub URL as well as a bare id — people paste what's in their
  // address bar, and rejecting it teaches nothing.
  if (value.startsWith("http://") || value.startsWith("https://")) {
    try {
      const { hostname, pathname } = new URL(value);
      if (!hostname.endsWith("huggingface.co")) {
        throw new Error(`${hostname} is not a Hugging Face URL`);
      }
      value = pathname.replace(/^\/+/, "").split("/").slice(0, 2).join("/");
    } catch (cause) {
      throw new Error("Could not read a repo id out of that URL", { cause });
    }
  }

  value = value.replace(/^\/+|\/+$/g, "");
  const parts = value.split("/");
  if (parts.length > 2 || parts.some((part) => part === "")) {
    throw new Error(
      `Expected a repo id of the form "owner/name", got "${input.trim()}"`,
    );
  }
  return value;
}
